import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import styles from './SchedulerWorker.module.scss';
import { schedulerActions } from '../../store';
import dayjs from 'dayjs';
import SchedulerTask from '../SchedulerTask';
import AvatarIcon from '../../../Common/AvatarIcon';
import { modalActions } from '../../../Modals/store';
import { ROUTES } from '../../../../routes/constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  Rocket,
  AlertTriangle,
  Users,
  CalendarClock,
  Armchair,
  Clock,
  BellRing,
  User,
} from 'lucide-react';
import IOSSwitch from '../../../Common/CustomSwitch';
import { getClientDisplayName } from '../utils/taskDisplay';
import WorkerChairMenu from './WorkerChairMenu';
import ConfirmModal from '../../../Common/ConfirmModal';
import useWindowSize from 'utils/useWindowSize';
import useOutsideClick from 'utils/useOutsideClick';
import clx from 'classnames';
import { getCalendarQueue } from '../../store/selectors';
import { useTranslation } from 'react-i18next';
import { DAY_HEADER_HEIGHT, SLOT_MINUTES, WORKER_HEADER_HEIGHT, getSlotIndexFromPointer, scaleLayoutPx } from '../../constants/layout';
import {
  getAvailableIntervals,
  getIntervalsDuration,
  getOccupiedDurationInAvailableSlots,
} from '../utils/closureHelpers';
import { subscribeSchedulerPointerDrag } from '../utils/pointerDragBus';

const CREATE_SNAP_MINUTES = 15;
const CREATE_DURATION_MINUTES = 30;
const SNAP_SLOTS = CREATE_SNAP_MINUTES / SLOT_MINUTES;
const PREVIEW_SLOTS = CREATE_DURATION_MINUTES / SLOT_MINUTES;

const formatDurationLabel = (totalSeconds, t) => {
  let minutes = Math.floor(totalSeconds / 60);
  let hours = Math.floor(minutes / 60);
  minutes = minutes % 60;
  hours = hours % 24;

  const hourPart =
    hours > 1
      ? `${hours} ${t('scheduler.time.hours')}`
      : hours === 1
        ? `1 ${t('scheduler.time.hour')}`
        : '';
  const minutePart =
    minutes > 1
      ? `${minutes} ${t('scheduler.time.minutes')}`
      : minutes === 1
        ? `1 ${t('scheduler.time.minute')}`
        : '';
  const joiner = hourPart && minutePart ? ` ${t('scheduler.schedulerWorker.status.and')} ` : '';

  return `${hourPart}${joiner}${minutePart}`.trim();
};

const SchedulerWorker = ({
  day,
  worker,
  setStartData,
  startDataRef,
  stepSize,
  startData,
  showHeader,
  singleChair = false,
  cardSpace,
  zoomScale = 1,
}) => {
  const { t } = useTranslation();
  const dayHeaderHeight = scaleLayoutPx(DAY_HEADER_HEIGHT, zoomScale);
  const [extraTask, setExtraTask] = useState({
    day,
    workerId: worker.id,
    task: {},
  });
  const [taskToDrag, setTaskToDrag] = useState(false);
  const [hoverSlot, setHoverSlot] = useState(null);
  const dropdownWorkerRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const modalType = useSelector((state) => state.modalsReducer?.modalData?.type);
  const addTaskModalWasOpenRef = useRef(false);

  // Overlay "Inizio" visibile in creazione; sparisce solo chiudendo il modal
  useEffect(() => {
    const isAddTaskOpen = modalType === 'addTaskModal';
    if (addTaskModalWasOpenRef.current && !isAddTaskOpen) {
      setHoverSlot(null);
    }
    addTaskModalWasOpenRef.current = isAddTaskOpen;
  }, [modalType]);
  const [searchParams] = useSearchParams();
  const calendarQueue = useSelector(getCalendarQueue());
  const workerQueue = useMemo(() => {
    const workers = calendarQueue?.workers || [];
    return workers.find((item) => +item.id === +worker.id) || null;
  }, [calendarQueue, worker.id]);
  const queueCount = workerQueue?.clients?.length || 0;
  const canManageChair =
    searchParams.get('salon_management') == 1 || !localStorage.getItem('staff');

  const openWorkerQueue = (event) => {
    event?.stopPropagation();
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'clientsQueueModal',
        props: {
          workers: workerQueue ? [workerQueue] : [],
          clientsLength: queueCount,
        },
      }),
    );
  };
  const setValue = (position = 0, type) => {
    const fromRef = startDataRef?.current;
    const activeStart =
      (fromRef?.type && fromRef) || (startData?.type && startData) || {};
    if (position >= 0) {
      const positionToTime = dayjs(day.startWork * 1000).add(position * 5, 'minutes');
      if (
        (activeStart.type || type) &&
        (day.date !== extraTask.day?.date ||
          worker.id !== extraTask.workerId ||
          position !== extraTask.task?.position)
      ) {
        switch (type || activeStart.type) {
          case 'create': {
            const newEndTaskTime = positionToTime.add(30, 'minutes');
            const isOverflow = day.endWork <= newEndTaskTime.unix();
            const task = {
              left: 0,
              hovered: true,
              color: 'gray',
              nome: worker.workerName,
            };
            if (isOverflow) {
              const maxPosition = (day.workTime - 1800) / 60 / 5;
              const maxEndTaskTime = dayjs(day.endWork * 1000);
              const minEndTaskTime = dayjs((day.endWork - 1800) * 1000);
              task.top = maxPosition * stepSize;
              task.position = maxPosition;
              task.orario_start = minEndTaskTime.format('HH:mm:ss');
              task.orario_end = maxEndTaskTime.format('HH:mm:ss');
              task.unixStartTime = minEndTaskTime.unix();
              task.unixEndTime = maxEndTaskTime.unix();
              task.size = ((task.unixEndTime - task.unixStartTime) / 60 / 5) * stepSize;
            } else {
              const minutes = positionToTime.get('minutes');
              const floorMinutes = Math.floor(minutes / 15) * 15;
              const roundedPosition = positionToTime.set('minutes', floorMinutes);
              const newEndTaskTime = roundedPosition.add(30, 'minutes');
              const diff = (floorMinutes - minutes) / 5;
              task.top = position * stepSize + stepSize * diff;
              task.position = position + diff;
              task.orario_start = roundedPosition.format('HH:mm:ss');
              task.orario_end = newEndTaskTime.format('HH:mm:ss');
              task.unixStartTime = roundedPosition.unix();
              task.unixEndTime = newEndTaskTime.unix();
              task.size = ((task.unixEndTime - task.unixStartTime) / 60 / 5) * stepSize;
            }
            dispatch(
              modalActions.OPEN_MODAL.SUCCESS({
                type: 'addTaskModal',
                props: {
                  ...extraTask,
                  task,
                  subtitle: dayjs(extraTask?.day?.date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
                },
              }),
            );
            setHoverSlot(typeof task.position === 'number' ? task.position : position);
            setStartData({});
            return setExtraTask({
              day,
              workerId: worker.id,
              task: {},
            });
          }
          case 'dragged': {
            if (!activeStart.task) return;
            const durationSeconds =
              activeStart.task.unixSize ||
              (activeStart.task.unixEndTime != null && activeStart.task.unixStartTime != null
                ? activeStart.task.unixEndTime - activeStart.task.unixStartTime
                : 0);
            if (!durationSeconds) return;
            const newEndTaskTime = positionToTime.add(durationSeconds, 'seconds');
            const isOverflow = day.endWork <= newEndTaskTime.unix();
            const task = {
              ...activeStart.task,
              dragged: true,
              isResizePreview: false,
              size:
                activeStart.task.size ??
                (durationSeconds / 60 / SLOT_MINUTES) * stepSize,
            };
            if (isOverflow) {
              const maxPosition = (day.workTime - durationSeconds) / 60 / 5;
              const maxEndTaskTime = dayjs(day.endWork * 1000);
              const minEndTaskTime = dayjs((day.endWork - durationSeconds) * 1000);
              task.top = maxPosition * stepSize;
              task.position = maxPosition;
              task.orario_start = minEndTaskTime.format('HH:mm:ss');
              task.orario_end = maxEndTaskTime.format('HH:mm:ss');
              task.unixStartTime = minEndTaskTime.unix();
              task.unixEndTime = maxEndTaskTime.unix();
            } else {
              task.top = position * stepSize;
              task.position = position;
              task.orario_start = positionToTime.format('HH:mm:ss');
              task.orario_end = newEndTaskTime.format('HH:mm:ss');
              task.unixStartTime = positionToTime.unix();
              task.unixEndTime = newEndTaskTime.unix();
            }
            return setExtraTask({
              day,
              workerId: worker.id,
              task,
            });
          }
          case 'resize': {
            if (!activeStart.task || activeStart.task.unixStartTime == null) return;
            const startUnix = activeStart.task.unixStartTime;
            const startTop =
              activeStart.task.top ??
              ((startUnix - day.startWork) / 60 / SLOT_MINUTES) * stepSize;
            const startSlot = Math.max(
              0,
              Math.round((startUnix - day.startWork) / 60 / SLOT_MINUTES),
            );
            const minEndSlot = startSlot + 1;
            let endSlot = Math.max(minEndSlot, position + 1);
            const dayEndSlot =
              day.endWork != null
                ? Math.floor((day.endWork - day.startWork) / 60 / SLOT_MINUTES)
                : endSlot;
            endSlot = Math.min(endSlot, dayEndSlot);
            const unixEndTime = day.startWork + endSlot * 60 * SLOT_MINUTES;
            const size = Math.max(stepSize, endSlot * stepSize - startTop);
            return setExtraTask({
              day,
              workerId: worker.id,
              task: {
                ...activeStart.task,
                top: startTop,
                position: endSlot,
                dragged: true,
                isResizePreview: true,
                orario_start: activeStart.task.orario_start,
                orario_end: dayjs(unixEndTime * 1000).format('HH:mm:ss'),
                unixStartTime: startUnix,
                unixEndTime,
                unixSize: unixEndTime - startUnix,
                size,
              },
            });
          }
        }
      }
    }
  };

  const addTask = (position) => {
    //prmesso account staff allowInsert deve essere = 1 se 'staff' in localStorage
    if(searchParams.get('allowInsert') != 1 && localStorage.getItem('staff')) return;

    position = Math.floor(position / SNAP_SLOTS) * SNAP_SLOTS;
    setHoverSlot(position);
    if (extraTask.task.position === position) {
      dispatch(
        modalActions.OPEN_MODAL.SUCCESS({
          type: 'addTaskModal',
          props: extraTask,
        }),
      );
      setStartData({});
      setExtraTask({
        day,
        workerId: worker.id,
        task: {},
      });
      return;
    }
    setStartData({
      type: 'create',
      workerId: worker.id,
      task: {},
      day,
    });
    setValue(position, 'create');
  };

  const saveDraggedTask = () => {
    dispatch(
      schedulerActions.EDIT_CALENDAR_TASK.REQUEST(taskToDrag, () => {
        setExtraTask({ day, workerId: worker.id, task: {} });
        setStartData({});
        setTaskToDrag(false);
      }),
    );
  };

  const tasks = worker.workerTasks.filter((t) => t.idprenotazione);
  const displayedDate = dayjs(day.date).format('DD/MM/YYYY');

  const hasOverlappingBookings = useMemo(
    () => worker.workerTasks.some((task) => task.isSplitOverlap || task.overlapHidden),
    [worker.workerTasks],
  );

  const workerStats = useMemo(() => {
    const dayTasks = worker.workerTasks.filter((task) => task.idprenotazione);
    const dayClosures = worker.workerTasks.filter((task) => task.limitCard === true);
    const visibleTasks = dayTasks.filter((task) => !task.overlapHidden);
    const availableIntervals = getAvailableIntervals(day.startWork, day.endWork, dayClosures);
    const availableTime = getIntervalsDuration(availableIntervals);
    const tasksMs = getOccupiedDurationInAvailableSlots(visibleTasks, availableIntervals);

    if (availableTime === 0) {
      return {
        percent: 0,
        soldOut: false,
        closed: true,
        label: t('scheduler.schedulerWorker.status.chairClosed'),
      };
    }

    if (visibleTasks.length === 0) {
      return {
        percent: 0,
        soldOut: false,
        closed: false,
        label: t('scheduler.schedulerWorker.status.noBookings'),
      };
    }

    const percent = Math.min((tasksMs / availableTime) * 100, 100);
    const dailyLeft = availableTime - tasksMs;
    const soldOut = tasksMs >= availableTime;
    const roundedPercent = Math.round(percent);

    let label = t('scheduler.schedulerWorker.status.soldOut');
    if (!soldOut) {
      if (dailyLeft > 0) {
        label = t('scheduler.schedulerWorker.status.timeLeft', {
          percent: roundedPercent,
          duration: formatDurationLabel(dailyLeft, t),
        });
      } else {
        label = t('scheduler.schedulerWorker.status.overtime', {
          duration: formatDurationLabel(Math.abs(dailyLeft), t),
        });
      }
    }

    return {
      percent: soldOut ? 100 : roundedPercent,
      soldOut,
      closed: false,
      label,
    };
  }, [worker.workerTasks, day.startWork, day.endWork, t]);

  const modalData = workerStats;
  const sOModalData = useMemo(
    () => ({
      worker,
      day,
      soldOut: workerStats.soldOut,
    }),
    [worker, day, workerStats.soldOut],
  );

  const openShareModal = (event) => {
    event?.stopPropagation();
    setIsDropdownOpen(false);
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'workerSoldOutModal',
        props: { worker, modalData: sOModalData, tasks },
      }),
    );
  };

  useOutsideClick(dropdownWorkerRef, (event) => {
    if (!isDropdownOpen) return;
    if (dropdownMenuRef.current?.contains(event?.target)) return;
    setIsDropdownOpen(false);
  });

  // Chiudi menu poltrona appena parte un drag/resize/create
  useEffect(() => {
    if (startData?.type && isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  }, [startData?.type, isDropdownOpen]);

  const updateMenuBox = useCallback(() => {
    const header = dropdownWorkerRef.current;
    if (!header) {
      setMenuBox(null);
      return;
    }
    const headerRect = header.getBoundingClientRect();
    const timeCol = document.querySelector('[class*="scheduler_time_line_container"]');
    const timeRight = timeCol ? timeCol.getBoundingClientRect().right : 0;
    const gap = 8;
    const minWidth = 260;
    // Mai sulla colonna orari; larghezza fissa comoda (può sporgere sulla poltrona accanto)
    const left = Math.max(headerRect.left, timeRight + gap);
    const width = Math.max(minWidth, headerRect.width);
    setMenuBox({
      top: headerRect.bottom + 6,
      left,
      width,
    });
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) {
      setMenuBox(null);
      return undefined;
    }
    updateMenuBox();
    const onScrollOrResize = () => updateMenuBox();
    window.addEventListener('resize', onScrollOrResize);
    // capture: aggiorna anche sullo scroll della griglia calendario
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [isDropdownOpen, updateMenuBox]);

  const cardWidth = menuBox?.width ?? dropdownWorkerRef?.current?.clientWidth;
  const maxDropdownWidth = 250;

  const [sendNotifica, setSendNotifica] = useState(false);
  const extraTaskRef = useRef(extraTask);
  extraTaskRef.current = extraTask;
  const workerContainerRef = useRef(null);
  const pointerDragHandlerRef = useRef(() => {});

  pointerDragHandlerRef.current = ({ phase, type, clientX, clientY, sourceWorkerId }) => {
    const el = workerContainerRef.current;
    if (!el) return;

    const fromRef = startDataRef?.current;
    const activeStart =
      (fromRef?.type && fromRef) || (startData?.type && startData) || {};
    if (!activeStart?.type && phase !== 'end') return;

    // Resize resta sulla poltrona di origine
    if (type === 'resize' && String(sourceWorkerId) !== String(worker.id)) return;

    const rect = el.getBoundingClientRect();
    const over =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;

    if (phase === 'move') {
      if (!over) {
        if (extraTaskRef.current?.task?.dragged) {
          setExtraTask((taskValue) => ({ ...taskValue, task: {} }));
        }
        return;
      }
      const position = getSlotIndexFromPointer(clientY, el, stepSize);
      setValue(position);
      return;
    }

    if (phase === 'end') {
      if (over) {
        const current = extraTaskRef.current;
        const formedTask = {
          idPrenotazione: current?.task?.idprenotazione,
          orario_start: current?.task?.orario_start,
          orario_end: current?.task?.orario_end,
          data_prenotazione: current?.day?.date,
          poltrona: current?.workerId,
        };
        if (formedTask.idPrenotazione) {
          setTaskToDrag(formedTask);
        } else if (current?.task?.dragged) {
          setExtraTask((taskValue) => ({ ...taskValue, task: {} }));
        }
      } else if (extraTaskRef.current?.task?.dragged) {
        setExtraTask((taskValue) => ({ ...taskValue, task: {} }));
      }
      setStartData({});
    }
  };

  // Mobile / touch: collega il bus pointer → setValue / drop (senza questo il drag non si muove)
  useEffect(
    () => subscribeSchedulerPointerDrag((payload) => pointerDragHandlerRef.current(payload)),
    [],
  );

  // Anteprima resize subito al dragstart (prima del primo dragOver)
  useEffect(() => {
    if (startData?.type !== 'resize') return;
    if (String(startData.workerId) !== String(worker.id)) return;
    if (!startData.task?.unixEndTime || startData.task?.unixStartTime == null) return;
    if (extraTaskRef.current?.task?.isResizePreview) return;
    const currentEndSlot = Math.max(
      1,
      Math.round((startData.task.unixEndTime - day.startWork) / 60 / SLOT_MINUTES),
    );
    setValue(Math.max(0, currentEndSlot - 1), 'resize');
  }, [startData?.type, startData?.task?.idprenotazione, startData?.workerId, worker.id]);

  const interactionSourceId =
    startData?.type === 'resize' || startData?.type === 'dragged'
      ? startData?.task?.idprenotazione
      : null;

  const toggleSendNotifica = (checked) => {
    setSendNotifica(checked);
    setTaskToDrag((pending) => (pending ? { ...pending, sendNotificaUpdateUser: checked } : pending));
  };

  const moveTask = extraTask?.task || {};
  const moveClientName = moveTask.idprenotazione ? getClientDisplayName(moveTask) : '';
  const moveDate = day?.date ? dayjs(day.date).format('ddd D MMM') : '';
  const moveTime =
    taskToDrag?.orario_start && taskToDrag?.orario_end
      ? `${String(taskToDrag.orario_start).slice(0, 5)} – ${String(taskToDrag.orario_end).slice(0, 5)}`
      : '';

  const MoveSummary = () => (
    <div className={styles.move_summary}>
      {moveClientName ? (
        <div className={styles.move_client}>
          <User size={14} strokeWidth={2.4} aria-hidden />
          <span>{moveClientName}</span>
        </div>
      ) : null}
      <div className={styles.move_slot}>
        <span className={styles.move_chip}>
          <Armchair size={14} strokeWidth={2.4} aria-hidden />
          {worker.workerName || worker.name}
        </span>
        <span className={styles.move_chip}>
          <Clock size={14} strokeWidth={2.4} aria-hidden />
          {moveDate}
          {moveTime ? ` · ${moveTime}` : ''}
        </span>
      </div>
      {moveTask.utente ? (
        <label className={styles.notice}>
          <span className={styles.notice_icon}>
            <BellRing size={16} strokeWidth={2.3} aria-hidden />
          </span>
          <span className={styles.notice_label}>
            {t('scheduler.schedulerWorker.confirmMove.notification')}
          </span>
          <IOSSwitch
            checked={sendNotifica}
            onChange={(e) => toggleSendNotifica(e.target.checked)}
          />
        </label>
      ) : null}
    </div>
  );

  return (
    <>
      <ConfirmModal
        isModalOpen={!!taskToDrag}
        setIsModalOpen={() => {
          setExtraTask({ day, workerId: worker.id, task: {} });
          setStartData({});
          setTaskToDrag(false);
          setSendNotifica(false);
        }}
        Icon={CalendarClock}
        title={t('scheduler.schedulerWorker.confirmMove.title')}
        subtitle={t('scheduler.schedulerWorker.confirmMove.subtitle')}
        cancelLabel={t('common.actions.cancel')}
        confirmLabel={t('scheduler.schedulerWorker.confirmMove.confirm')}
        action={() => {
          saveDraggedTask();
          sendNotifica && setSendNotifica(false);
        }}
        content={<MoveSummary />}
      />
      <div
        className={styles.worker__wrapper}
        style={{
          ...(cardSpace ? { minWidth: cardSpace, flexBasis: cardSpace } : null),
          '--calendar-zoom': zoomScale,
          '--worker-header-height': `${scaleLayoutPx(WORKER_HEADER_HEIGHT, zoomScale)}px`,
        }}
      >
        <div
          data-nomediv={'worker_header'}
          draggable={false}
          className={clx(styles.worker_header, {
            [styles.worker_header_overlap]: hasOverlappingBookings,
            [styles.worker_header_open]: isDropdownOpen,
          })}
          style={{
            top: !showHeader ? '0px' : `${dayHeaderHeight}px`,
          }}
          ref={dropdownWorkerRef}
          onClick={() => {
            dispatch(schedulerActions.GET_CALENDAR_SLOTS.CLEAR());

            if (isMobile)
              dispatch(
                modalActions.OPEN_MODAL.SUCCESS({
                  type: 'workerRetailModal',
                  props: {
                    worker,
                    modalData: {
                      ...modalData,
                      salon_management: searchParams.get('salon_management'),
                    },
                    tasks,
                    sOModalData,
                    hasOverlappingBookings,
                    displayedDate,
                    queueCount,
                    canManageChair,
                    workerQueue,
                  },
                }),
              );
            else {
              setIsDropdownOpen(!isDropdownOpen);
            }
          }}
        >
          {/* <span data-nomediv={'worker_header'}></span> */}
          {worker.icon && !isMobile ? (
            <div className={styles.value_icon}>
              <AvatarIcon icon={worker.icon} name={worker.workerName} size={'28px'} />
            </div>
          ) : null}
          <div className={styles.header_main}>
            <div className={styles.option_value}>{worker.workerName}</div>
            {hasOverlappingBookings || queueCount > 0 || sOModalData.soldOut ? (
              <div className={styles.header_actions}>
                {hasOverlappingBookings ? (
                  <span
                    className={clx(styles.overlap_badge, {
                      [styles.overlap_badge_compact]: isMobile,
                    })}
                    title={t('scheduler.schedulerWorker.overlap.short')}
                  >
                    <AlertTriangle size={11} strokeWidth={2.4} />
                    {!isMobile ? (
                      <span className={styles.overlap_badge_label}>
                        {t('scheduler.schedulerWorker.overlap.short')}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                {queueCount > 0 ? (
                  <button
                    type="button"
                    className={clx(styles.queue_badge, {
                      [styles.queue_badge_compact]: isMobile,
                    })}
                    title={t('scheduler.schedulerWorker.queue.badge', { count: queueCount })}
                    onClick={openWorkerQueue}
                  >
                    <Users size={11} strokeWidth={2.4} />
                    {!isMobile ? (
                      <span className={styles.queue_badge_label}>
                        {t('scheduler.schedulerWorker.queue.badge', { count: queueCount })}
                      </span>
                    ) : (
                      <span className={styles.queue_badge_count}>{queueCount}</span>
                    )}
                  </button>
                ) : null}
                {sOModalData.soldOut ? (
                  <button
                    type="button"
                    className={clx(styles.sold_out_badge, {
                      [styles.sold_out_badge_compact]: isMobile && !singleChair,
                    })}
                    title={t('scheduler.schedulerWorker.dropdown.shareInstagram')}
                    onClick={openShareModal}
                  >
                    <Rocket size={12} strokeWidth={2.4} />
                    {!isMobile || singleChair
                      ? t('scheduler.schedulerWorker.status.soldOut')
                      : null}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          <div
            className={clx(styles.menu_icon, {
              [styles.menu_icon_open]: isDropdownOpen,
            })}
          >
            <ChevronDown size={16} strokeWidth={2.2} />
          </div>
        </div>

        {!isMobile &&
          isDropdownOpen &&
          menuBox &&
          createPortal(
            <div
              ref={dropdownMenuRef}
              className={styles.dropdown__menu_portal}
              style={{
                top: menuBox.top,
                left: menuBox.left,
                width: menuBox.width,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className={styles.dropdown__menu_inner_list}>
                <WorkerChairMenu
                  hasOverlappingBookings={hasOverlappingBookings}
                  modalData={modalData}
                  tasks={tasks}
                  queueCount={queueCount}
                  canManageChair={canManageChair}
                  manageLabel={
                    cardWidth < maxDropdownWidth
                      ? t('scheduler.schedulerWorker.dropdown.manage')
                      : t('scheduler.schedulerWorker.dropdown.manageChair')
                  }
                  onShare={openShareModal}
                  onQueue={openWorkerQueue}
                  onManage={() => {
                    setIsDropdownOpen(false);
                    navigate(
                      `${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.WORKING_TIME}/${worker.id}`,
                    );
                  }}
                />
              </div>
            </div>,
            document.body,
          )}

        <div
          onDragLeave={(e) => {
            // Solo se si esce davvero dal container (non tra figli)
            if (e.currentTarget.contains(e.relatedTarget)) return;
            e.preventDefault();
            e.stopPropagation();
            const active = (startDataRef && startDataRef.current) || startData || {};
            // Preview resta fino a drop/end (dragleave tra figli o pre-drop azzerava il ghost)
            if (active.type === 'resize' || active.type === 'dragged') return;
            setExtraTask((taskValue) => ({ ...taskValue, task: {} }));
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const position = getSlotIndexFromPointer(e.clientY, e.currentTarget, stepSize);
            const active = (startDataRef && startDataRef.current) || startData || {};
            if (active.type === 'resize' || active.type === 'dragged' || active.type === 'create') {
              setValue(position);
              return;
            }
            const target = e.nativeEvent.target;
            const dragRole = target?.dataset?.dragRole;
            if (dragRole === 'slot' || target?.classList?.contains('worker')) {
              setValue(position);
            }
          }}
          onDragExit={(e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            e.preventDefault();
            e.stopPropagation();
            const active = (startDataRef && startDataRef.current) || startData || {};
            if (active.type === 'resize' || active.type === 'dragged') return;
            setExtraTask((taskValue) => ({ ...taskValue, task: {} }));
          }}
          onDragEndCapture={() => {
            // Evitare clear aggressivo: azzerare extraTask qui rompe il confirm dopo drop
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const current = extraTaskRef.current;
            const formedTask = {
              idPrenotazione: current?.task?.idprenotazione,
              orario_start: current?.task?.orario_start,
              orario_end: current?.task?.orario_end,
              data_prenotazione: current?.day?.date,
              poltrona: current?.workerId,
            };
            if (formedTask.idPrenotazione) {
              setTaskToDrag(formedTask);
            }
          }}
          onDragEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setStartData({});
            // Drop gira prima di dragend: se non c’è confirm, pulisci la preview
            setTimeout(() => {
              setTaskToDrag((pending) => {
                if (!pending) {
                  setExtraTask((taskValue) => ({ ...taskValue, task: {} }));
                }
                return pending;
              });
            }, 0);
          }}
          className={styles.worker_container}
          ref={workerContainerRef}
        >
          <div
            onClick={(e) => addTask(getSlotIndexFromPointer(e.clientY, e.currentTarget, stepSize))}
            onMouseMove={(e) => {
              // Su mobile niente preview da mouseMove sintetico: overlay solo al tap/creazione
              if (isMobile || modalType === 'addTaskModal') return;
              if (startData?.type === 'dragged' || startData?.type === 'resize') {
                if (hoverSlot !== null) setHoverSlot(null);
                return;
              }
              const raw = getSlotIndexFromPointer(e.clientY, e.currentTarget, stepSize);
              const snapped = Math.floor(raw / SNAP_SLOTS) * SNAP_SLOTS;
              const totalSlots = Math.floor(day.workTime / 60 / SLOT_MINUTES);
              const maxStart = Math.max(0, totalSlots - PREVIEW_SLOTS);
              const next = Math.min(Math.max(snapped, 0), maxStart);
              if (next !== hoverSlot) setHoverSlot(next);
            }}
            onMouseLeave={() => {
              // Non togliere l'overlay mentre il modal di creazione è aperto
              if (modalType === 'addTaskModal') return;
              setHoverSlot(null);
              if (startData.type === 'create') {
                setExtraTask((taskValue) => ({ ...taskValue, task: {} }));
                setStartData({});
              }
            }}
            className={clx('worker', styles.empty_slot)}
            data-drag-role="slot"
          ></div>
          {hoverSlot !== null && (
            <div
              className={styles.slot_hover}
              style={{
                top: hoverSlot * stepSize,
                height: PREVIEW_SLOTS * stepSize,
              }}
            >
              <span className={styles.slot_hover_label}>
                {t('scheduler.schedulerWorker.slotHover.start', {
                  time: dayjs(day.startWork * 1000)
                    .add(hoverSlot * SLOT_MINUTES, 'minutes')
                    .format('HH:mm'),
                })}
              </span>
            </div>
          )}
          {worker.id === extraTask.workerId &&
          day.date === extraTask.day.date &&
          !!Object.keys(extraTask.task).length ? (
            <SchedulerTask
              setStartData={setStartData}
              selectedTask={extraTask.task}
              day={day}
              workerId={worker.id}
              stepSize={stepSize}
              searchParams={searchParams}
            />
          ) : null}
          {worker?.workerTasks?.map((selectedTask, index) => (
            <SchedulerTask
              key={index}
              setStartData={setStartData}
              selectedTask={
                interactionSourceId &&
                selectedTask.idprenotazione === interactionSourceId
                  ? { ...selectedTask, isInteractionSource: true }
                  : selectedTask
              }
              day={day}
              workerId={worker.id}
              stepSize={stepSize}
              searchParams={searchParams}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default SchedulerWorker;
